import React, { useEffect, useState } from 'react';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import '../../component-styles/EmailBrandingSettings.css';

const DEFAULTS = {
  primaryColor: '#a5d27d',
  buttonTextColor: '#1a1a1a',
  signatureText: '',
  senderName: '',
  replyToEmail: '',
};

export default function EmailBrandingSettings() {
  const userId = localStorage.getItem('userId');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULTS);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${userId}`);
        const data = await res.json();
        setForm({
          primaryColor: data.emailBranding?.primaryColor || DEFAULTS.primaryColor,
          buttonTextColor: data.emailBranding?.buttonTextColor || DEFAULTS.buttonTextColor,
          signatureText: data.emailBranding?.signatureText || DEFAULTS.signatureText,
          senderName: data.emailBranding?.senderName || data.company?.companyName || DEFAULTS.senderName,
          replyToEmail: data.emailBranding?.replyToEmail || data.company?.email || DEFAULTS.replyToEmail,
        });
      } catch (err) {
        console.error('Failed to load email branding settings', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('idToken');
      const res = await fetch(`${API_BASE}/api/settings/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ emailBranding: form }),
      });
      if (!res.ok) throw new Error('Save failed');
      notify('Email branding saved!');
    } catch (err) {
      console.error('Save email branding failed', err);
      notify('Failed to save email branding.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading...</p>;

  const previewBg = form.primaryColor || DEFAULTS.primaryColor;
  const previewBtnText = form.buttonTextColor || DEFAULTS.buttonTextColor;

  return (
    <div className="eb-settings">
      <p className="eb-desc">
        Customize how your client-facing emails look. These defaults apply whenever you send an invoice or proposal — you can also override them per-send from the compose window.
      </p>

      <div className="eb-grid">
        {/* Left — Controls */}
        <div className="eb-controls">
          <div className="form-row">
            <label>Header &amp; Button Color</label>
            <div className="eb-color-row">
              <input
                type="color"
                value={form.primaryColor}
                onChange={(e) => set('primaryColor', e.target.value)}
                className="eb-color-swatch"
              />
              <input
                type="text"
                value={form.primaryColor}
                onChange={(e) => set('primaryColor', e.target.value)}
                maxLength={7}
                className="eb-color-hex"
                placeholder="#a5d27d"
              />
            </div>
          </div>

          <div className="form-row">
            <label>Button Text Color</label>
            <div className="eb-color-row">
              <input
                type="color"
                value={form.buttonTextColor}
                onChange={(e) => set('buttonTextColor', e.target.value)}
                className="eb-color-swatch"
              />
              <input
                type="text"
                value={form.buttonTextColor}
                onChange={(e) => set('buttonTextColor', e.target.value)}
                maxLength={7}
                className="eb-color-hex"
                placeholder="#1a1a1a"
              />
            </div>
          </div>

          <div className="form-row">
            <label>Sender Name</label>
            <input
              type="text"
              value={form.senderName}
              onChange={(e) => set('senderName', e.target.value)}
              placeholder="e.g. Katie Gelsheimer Design"
            />
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Shown as the "From" name on client emails, so it reads as coming from your studio instead of a generic address.
            </p>
          </div>

          <div className="form-row">
            <label>Reply-To Email</label>
            <input
              type="email"
              value={form.replyToEmail}
              onChange={(e) => set('replyToEmail', e.target.value)}
              placeholder="you@yourstudio.com"
            />
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Client replies go here instead of getting lost at a no-reply address.
            </p>
          </div>

          <div className="form-row">
            <label>Signature / Closing Text</label>
            <textarea
              rows={4}
              value={form.signatureText}
              onChange={(e) => set('signatureText', e.target.value)}
              placeholder={`e.g.\nBest regards,\nYour Studio Name\n(555) 123-4567`}
              style={{ width: '100%', resize: 'vertical' }}
            />
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Appended at the bottom of each client email.
            </p>
          </div>

          <button className="save-btn" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* Right — Live Preview */}
        <div className="eb-preview">
          <p className="eb-preview-label">Preview</p>
          <div className="eb-preview-card">
            <div className="eb-preview-header" style={{ background: previewBg }}>
              <span className="eb-preview-logo">Your Company Name</span>
            </div>
            <div className="eb-preview-body">
              <p className="eb-preview-greeting">Hi Client Name,</p>
              <p className="eb-preview-text">
                Your designer has sent you invoice <strong>#IN-00001</strong> for your review. Click the button below to view and pay at your convenience.
              </p>
              <div
                className="eb-preview-btn"
                style={{ background: previewBg, color: previewBtnText }}
              >
                View Invoice
              </div>
              {form.signatureText && (
                <p className="eb-preview-signature">{form.signatureText}</p>
              )}
            </div>
            <div className="eb-preview-footer">Sent via InteriorFolio</div>
          </div>
        </div>
      </div>
    </div>
  );
}
